import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppDispatch, useAppStore } from "@/store/store-hooks";
import {
  updateProjectWithUnplaced2dPhotosData,
  UpdateProjectWithUnplaced2dPhotosDataParams,
} from "@/utils/use-import-2d-photos";
import {
  UploadedFileData,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useCallback, useRef } from "react";
import { useMultiFileUploader } from "../file-upload-context/use-file-uploader";
import {
  updateProjectWithNewWaypointAndImageData,
  UpdateProjectWithNewWaypointAndImageDataParams,
} from "../point-cloud-file-upload-context/image-data-new-waypoint-upload-utils";
import {
  updateProjectWithImageData,
  UpdateProjectWithImageDataParams,
} from "../point-cloud-file-upload-context/image-data-upload-utils";
import {
  CommonUpdateProjectMultiFilesParams,
  UploadElementType,
} from "../point-cloud-file-upload-context/use-upload-element-types";

type UploadMultiFilesCallbackArgs = TypeSpecificUpdateProjectParams &
  Pick<CommonUpdateProjectMultiFilesParams, "areaId"> & {
    /** The main file to upload */
    files: File[];

    /** Additional files to upload. These uploads have to complete before the element is created. */
    additionalFiles?: File[];
  };

/**
 * Callback returned by the useUploadMultiFiles hook.
 */
type UploadMultiFilesCallback = (
  args: UploadMultiFilesCallbackArgs,
) => Promise<void>;

/**
 * @returns A callback to upload multiple image files to the backend and add them to the project
 */
export function useUploadMultiFiles(): UploadMultiFilesCallback {
  const uploadMultiFiles = useMultiFileUploader();
  const appStore = useAppStore();
  const dispatch = useAppDispatch();
  const { projectApiClient, coreApiClient } = useApiClientContext();
  const { handleErrorWithDialog } = useErrorHandlers();

  // A promise capturing the current project update
  // as only one update can happen at a time as the first update
  // will have the side effect of creating the parent nodes
  // required by the follow up updates
  const updateProjectPromise = useRef<Promise<void>>(undefined);

  return useCallback<UploadMultiFilesCallback>(
    async ({ files, additionalFiles = [], areaId, ...updateProjectParams }) => {
      const projectToken = await coreApiClient.getUserProjectToken(
        projectApiClient.projectId,
      );
      const additionalUploadPromise = new Promise<string[]>(
        (resolve, reject) => {
          uploadMultiFiles({
            files: additionalFiles,
            uploadElementType: updateProjectParams.type,
            silent: true,
            coreApiClient,
            projectId: projectApiClient.projectId,
            projectToken,
            onUploadCompleted: (_, uploadFilesData) => {
              // The uploadFilesData have the same size and the same order as the files passed to uploadMultiFiles
              resolve(uploadFilesData.map((data) => data.downloadUrl));
            },
            onUploadFailed: reject,
          });
        },
      );

      async function finalizeDownload(
        uploadedFilesData: UploadedFileData[],
      ): Promise<void> {
        const additionalUrls = await additionalUploadPromise;

        // Allow only one project update at a time
        // as the first project update on an area will create the parent elements
        // required by the other updates
        if (updateProjectPromise.current) {
          await Promise.allSettled([updateProjectPromise.current]);
        }

        // eslint-disable-next-line require-atomic-updates
        updateProjectPromise.current = updateProjectWithMultiFiles({
          appStore,
          dispatch,
          projectApi: projectApiClient,
          files,
          areaId,
          uploadedFilesData,
          additionalUrls,
          ...updateProjectParams,
        }).catch((error) => {
          // The task store will lose the error instance, so we show a dialog with more information about the failure here.
          handleErrorWithDialog({
            title: "Upload multiple images",
            error,
          });
          throw error;
        });

        return updateProjectPromise.current;
      }

      return new Promise((resolve, reject) =>
        uploadMultiFiles({
          files,
          silent: false,
          uploadElementType: updateProjectParams.type,
          projectId: projectApiClient.projectId,
          coreApiClient,
          projectToken,
          finalizer: finalizeDownload,
          iElementId:
            // If available, associate the upload task to a more specific element than the area
            "parentElementId" in updateProjectParams
              ? updateProjectParams.parentElementId
              : areaId,
          onUploadCompleted: () => resolve(),
          onUploadFailed: (error) => reject(error),
        }),
      );
    },
    [
      coreApiClient,
      projectApiClient,
      uploadMultiFiles,
      appStore,
      dispatch,
      handleErrorWithDialog,
    ],
  );
}

type TypeSpecificUpdateProjectParams =
  | UpdateProjectWithImageDataParams
  | UpdateProjectWithNewWaypointAndImageDataParams
  | UpdateProjectWithUnplaced2dPhotosDataParams;

type UpdateProjectParams = CommonUpdateProjectMultiFilesParams &
  TypeSpecificUpdateProjectParams;

function updateProjectWithMultiFiles(
  params: UpdateProjectParams,
): Promise<void> {
  switch (params.type) {
    case UploadElementType.imageData:
      return updateProjectWithImageData(params);
    case UploadElementType.waypointWithImages:
      return updateProjectWithNewWaypointAndImageData(params);
    case UploadElementType.unplaced2dPhotos:
      return updateProjectWithUnplaced2dPhotosData(params);
  }
}
